/**
 * SystemPostTemplateService — read-only Firestore client for the
 * nested `system_platforms/{platformId}/post_templates/*` catalogue.
 *
 * Used by the Templates browse tab in ContentPlanPage. Does NOT read or
 * write the per-project overlay (`projects/{pid}/post_templates/*`) —
 * that's handled in a future iteration when authoring UI lands.
 *
 * The catalogue is seeded by `scripts/seed-system-post-templates.ts`
 * which parses `n8n/contentPlan/references/post-goals-templates.md`.
 */

import {
  collection,
  collectionGroup,
  getDocs,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  type Unsubscribe,
  type QueryConstraint,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { PostTemplate } from '@segmently/schema-models/content-plan';

export interface ListFilter {
  goal?: string;
  platform?: string;
  tone?: string;
  lengthBucket?: 'short' | 'medium' | 'long';
}

export class SystemPostTemplateService {
  private readonly db = CoreFirebaseService.getInstance().firestore;

  async list(filter: ListFilter = {}): Promise<PostTemplate[]> {
    const ref = filter.platform
      ? collection(this.db, 'system_platforms', filter.platform, 'post_templates')
      : collectionGroup(this.db, 'post_templates');
    const constraints = this.buildConstraints(filter);
    const snap = await getDocs(query(ref, ...constraints));
    const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as PostTemplate));
    return this.applyClientSideFilters(docs, filter);
  }

  async getById(templateId: string, platformId?: string): Promise<PostTemplate | null> {
    if (!platformId) {
      const snap = await getDocs(query(
        collectionGroup(this.db, 'post_templates'),
        where('scope', '==', 'system'),
        where('id', '==', templateId),
      ));
      const first = snap.docs[0];
      return first ? ({ id: first.id, ...first.data() } as PostTemplate) : null;
    }
    const ref = doc(this.db, 'system_platforms', platformId, 'post_templates', templateId);
    const snap = await getDoc(ref);
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as PostTemplate) : null;
  }

  /**
   * Real-time subscription. Caller passes onChange (full filtered list) and
   * optional onError (surfaced from Firestore — permission errors etc.).
   * Returns the unsubscribe function.
   */
  subscribe(
    filter: ListFilter,
    onChange: (list: PostTemplate[]) => void,
    onError?: (err: Error) => void,
  ): Unsubscribe {
    const ref = filter.platform
      ? collection(this.db, 'system_platforms', filter.platform, 'post_templates')
      : collectionGroup(this.db, 'post_templates');
    const constraints = this.buildConstraints(filter);
    return onSnapshot(
      query(ref, ...constraints),
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as PostTemplate));
        onChange(this.applyClientSideFilters(docs, filter));
      },
      (err) => {
        // Surface Firestore errors (permission denied, network) instead of
        // hanging in "Loading…" state forever.
        // eslint-disable-next-line no-console
        console.error('[SystemPostTemplateService] subscribe error:', err);
        onError?.(err);
      },
    );
  }

  private buildConstraints(filter: ListFilter): QueryConstraint[] {
    const out: QueryConstraint[] = [where('scope', '==', 'system')];
    if (filter.goal) out.push(where('goal', '==', filter.goal));
    // platform/tone/lengthBucket filtered client-side to avoid composite indexes
    return out;
  }

  private applyClientSideFilters(list: PostTemplate[], filter: ListFilter): PostTemplate[] {
    return list.filter((t) => {
      if (filter.platform && t.platform !== filter.platform && t.platform !== 'any') return false;
      if (filter.tone && t.tone !== filter.tone) return false;
      if (filter.lengthBucket && t.lengthBucket !== filter.lengthBucket) return false;
      return true;
    });
  }
}

let instance: SystemPostTemplateService | null = null;
export function getSystemPostTemplateService(): SystemPostTemplateService {
  if (!instance) instance = new SystemPostTemplateService();
  return instance;
}
